'use client';

import { useLayoutEffect, useMemo, useRef } from 'react';
import ToggleButton, { ToggleElementType, type ToggleButtonChangeEventHandler } from '@pixum/toggle-button';
import { Icon } from '@pixum/icon';
import { isGermany, isDK } from 'utils';
import { useCartAddressFormStore } from 'stores';
import { DESTINATION_TYPES, isDestinationType } from '../../constants';
import type { DestinationToggleSectionClientProps, DestinationType } from '../../types';
import styles from '../../CartAddressStep.module.css';

function resolveDestinationType(
  isDestinationTypeInitialized: boolean,
  currentDestinationType: DestinationType,
  initialDestinationType?: DestinationType
): DestinationType {
  if (isDestinationTypeInitialized) {
    return currentDestinationType;
  }

  return initialDestinationType ?? currentDestinationType;
}

/**
 * Renders the private, company, and locale-specific pickup-point choices.
 * @param props - Localized constants, locale, and the address form content.
 * @returns The destination type toggle and its associated address content.
 */
export function DestinationToggleSectionClient({
  constants,
  initialDestinationType,
  locale,
  children
}: DestinationToggleSectionClientProps) {
  const destinationType: DestinationType = useCartAddressFormStore(state => state.destinationType);
  const isDestinationTypeInitialized = useCartAddressFormStore(state => state.isDestinationTypeInitialized);
  const setDestinationType = useCartAddressFormStore(state => state.setDestinationType);
  const clearShippingFieldErrors = useCartAddressFormStore(state => state.clearShippingFieldErrors);
  const clearBillingFieldErrors = useCartAddressFormStore(state => state.clearBillingFieldErrors);
  const focusedOptionId = useRef<string | null>(null);

  const toggleOptions = useMemo(
    () => [
      {
        id: DESTINATION_TYPES.PRIVATE,
        label: `${constants['i18n_private']}${isDK(locale) ? ` ${constants['i18n_private_address_surcharge']}` : ''}`,
        icon: <Icon name="home" ariaHidden={true} />
      },
      {
        id: DESTINATION_TYPES.COMPANY,
        label: constants['i18n_company'],
        icon: <Icon name="company" ariaHidden={true} />
      },
      ...(isGermany(locale) || isDK(locale)
        ? [
            {
              id: DESTINATION_TYPES.PICKUP_POINT,
              label: constants['i18n_packstation'],
              icon: <Icon name="packstation" ariaHidden={true} />
            }
          ]
        : [])
    ],
    [constants, locale]
  );

  const resolvedDestinationType = resolveDestinationType(
    isDestinationTypeInitialized,
    destinationType,
    initialDestinationType
  );
  // Use the saved type only until the client store has been initialized, then preserve user changes.
  const selectedOption = toggleOptions.find(option => option.id === resolvedDestinationType);

  useLayoutEffect(() => {
    // Re-rendering the keyed toggle can move focus; restore it after the new option is mounted.
    if (!focusedOptionId.current) {
      return;
    }

    document.getElementById(focusedOptionId.current)?.focus();
    focusedOptionId.current = null;
  }, [resolvedDestinationType]);

  const handleDestinationTypeChange: ToggleButtonChangeEventHandler = async (option, event) => {
    clearShippingFieldErrors();
    clearBillingFieldErrors();
    focusedOptionId.current = event.currentTarget.id;

    // ToggleButton accepts generic option ids, so validate the value before writing it to the typed store.
    if (typeof option.id === 'string' && isDestinationType(option.id)) {
      setDestinationType(option.id);
    }
  };

  return (
    <fieldset className={styles.cartAddressSection}>
      <legend className={`${styles.cartAddressLegend} text--title3`}>{constants['i18n_shipping_address']}</legend>
      <ToggleButton
        key={`destination-type-${resolvedDestinationType}`}
        groupName="destination-type"
        className={styles.cartAddressToggle}
        toggleElementType={ToggleElementType.INPUT}
        options={toggleOptions}
        optionToBeSet={selectedOption}
        onChange={handleDestinationTypeChange}
        label={constants['i18n_shipping_address']}
      />
      {children}
    </fieldset>
  );
}
